/**
 * layout — the person page's binding of the shared detail-page surface.
 *
 * A person has three panes over their canvas, ALL DOCKED RIGHT:
 *
 *   membership  the teams and departments they belong to               (right)
 *   activity    the audit log for this user                            (right)
 *   detail      the selected matter or task                            (right)
 *
 * The centre is the CANVAS — the tab bar and whichever tab is open (Profile,
 * Matters, Projects, Tasks).
 *
 * THERE IS NO CONTEXT PANE. It held the identity block, contact, availability,
 * rate/experience/hours and languages — every one of which is a fact about the
 * person, which is what the PROFILE tab is. Holding them in a left pane meant
 * the Profile tab was one thin card (bio, specializations, memberships) beside
 * a pane carrying everything else, and the person's own page could not be read
 * without opening a pane. The facts are the Profile canvas now (see
 * `EmployeeDetail`), the badges that mattered from other tabs are in
 * `subHeaderRight`, and the left dock is empty — `DetailPageSurface` renders
 * no column for a dock with no visible pane, so the canvas simply starts at
 * the left gutter.
 *
 * A layout stored before this change still names a `context` slot.
 * `validateLayout` rebuilds slots from the surface's OWN ids, so an id that no
 * longer exists is dropped on read and never reaches the shell — see the test.
 *
 * WHAT THIS REPLACES. The page was a single scrolling column: a bordered
 * header block, then a `md:grid-cols-2` of two cards, and nothing else. It
 * had no tabs at all, so a person's assigned matters and tasks — which the
 * external-collaborator page has always shown — were unreachable from the
 * internal one, and `EmployeeCaseDetailPanel` / `EmployeeTaskDetailPanel`
 * were components only the external page could open.
 *
 * MEMBERSHIP IS THE ACCESS PANE'S SLOT, NOT ITS COMPONENT. On a team or a
 * project the right-hand roster answers "who is on this record". A person is
 * not a record people are on — they are the person — so the slot answers the
 * mirror question: which teams and departments is this person on. Same dock,
 * same rail position, different body. See `PersonMembershipPane`.
 *
 * THE DETAIL PANE HAS NO RAIL TOGGLE. It opens because a row was selected
 * (`detailPane` in the config, driven by `useDetailSurface`), and a toggle
 * for it would read as a control that does nothing until you use the page.
 *
 * WHY ENTITY SCOPE. One shared key would make every person inherit the
 * arrangement of whoever was opened last.
 *
 * NO LEGACY KEYS — this surface is new. NO READER PANE — nothing here opens a
 * document in place.
 */

import {
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
} from '../../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../../shared/surface/defineDetailSurface';

export type PersonPanelId = 'membership' | 'activity' | 'detail';

/** What the detail pane is currently showing. */
export type PersonSelectionKind = 'matter' | 'task';

/** The canvas's tabs. Labels are the page's — see `EmployeeDetail`. */
export type PersonTab = 'Profile' | 'Matters' | 'Projects' | 'Tasks';

export const PERSON_TABS: PersonTab[] = ['Profile', 'Matters', 'Projects', 'Tasks'];

export type PersonPanelSlot = GenericSlot<PersonPanelId>;
export type PersonPanelLayout = GenericLayout<PersonPanelId>;

export const PERSON_PANEL_IDS: PersonPanelId[] = [
  'membership',
  'activity',
  'detail',
];

const PERSON_PANES: Record<PersonPanelId, DetailPaneConfig> = {
  membership: {
    label: 'Teams & Departments',
    bounds: { min: 260, max: 640 },
    dock: 'right', index: 0, width: 320, visible: false, rail: true,
  },
  activity: {
    label: 'Activity',
    bounds: { min: 280, max: 760 },
    dock: 'right', index: 1, width: 340, visible: false, rail: true,
  },
  detail: {
    label: 'Details',
    bounds: { min: 320, max: 760 },
    dock: 'right', index: 2, width: 400, visible: false,
  },
};

export const PERSON_SURFACE = defineDetailSurface<PersonPanelId, PersonTab>({
  kind: 'detail',
  // Also the `data-testid` prefix for the shell: `person-surface`, etc.
  name: 'person',
  scope: 'entity',
  ids: PERSON_PANEL_IDS,
  panes: PERSON_PANES,
  tabs: PERSON_TABS,
  detailPane: 'detail',
});

/* ── derived, for call sites and tests that want the plain records ── */

export const PERSON_PANEL_LABELS: Record<PersonPanelId, string> = PERSON_SURFACE.labels;
export const PERSON_PANEL_BOUNDS: Record<PersonPanelId, PanelBounds> = PERSON_SURFACE.bounds;
export const PERSON_DEFAULT_LAYOUT: PersonPanelLayout = PERSON_SURFACE.defaults;
